import { DropdownButtonContext } from '@dixa/lib-shared-habla';
import { conversationLanguages } from 'lib-shared-languages';
import { memo, useContext } from 'react';
import { useMount } from 'react-use';

import { useCopilotActionRunner } from 'components/copilotActions';
import { useEditorContext } from 'utils/useEditorContext';

import { EditorCopilotDropdownTranslate } from '../translate';

interface EditorCopilotDropdownTranslateToConversationLanguageProps {
  languageCode: string;
  csid: string;
}

/**
 * Represents a component that translates the selection into conversation
 * language. Falls back to the custom translate panel when the conversation
 * language cannot be determined.
 */
export const EditorCopilotDropdownTranslateToConversationLanguage = memo(
  function EditorCopilotDropdownTranslateToConversationLanguage({
    languageCode,
    csid,
  }: EditorCopilotDropdownTranslateToConversationLanguageProps) {
    const dropdownContext = useContext(DropdownButtonContext);
    const { getSelectedText } = useEditorContext();
    const { run } = useCopilotActionRunner({
      context: { actions: ['translateToConversationLanguage'] },
      text: '',
      csid,
      languageCode,
      // Closing the dropdown unmounts this component while the request is still
      // in flight, so it must not be cancelled.
      abortOnUnmount: false,
    });

    const language = conversationLanguages.find(({ code }) =>
      languageCode.startsWith(code),
    )?.name;

    useMount(() => {
      if (!language || !getSelectedText()) {
        return;
      }

      dropdownContext.closeDropdown();
      run('translateToConversationLanguage');
    });

    if (language) {
      return null;
    }

    return (
      <EditorCopilotDropdownTranslate
        csid={csid}
        initialText="Unable to detect conversation's language. Please select a language from the list above."
      />
    );
  },
);
